/**
 * LLM Results Explainer
 *
 * Generates a rich, contextual explanation of session results incorporating:
 * - Rankings and scores (from session report)
 * - Participant vote distribution (who agreed/disagreed on what)
 * - Item relationships (blocks, dependencies, clusters that influenced ranking)
 * - Session context (cycle, track, configuration, participants)
 * - Criteria and their weights
 *
 * Registry: results_explainer — on-demand narrator. Transport: callFeatureLlm.
 * Advisory only. Never modifies session state or rankings.
 * Source: DOCS_V4/00-canon/10O-cursor-session-results-explanation-and-reports.md
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

const RESULTS_EXPLAINER_SYSTEM_PROMPT =
  'You explain prioritization session results to stakeholders. Respond with valid JSON only.';

export interface RankedItemDetail {
  rank: number;
  itemId: string;
  title: string;
  score?: number;
  votes?: number;
  scoreDistribution?: {
    min: number;
    max: number;
    stdDev?: number;
  };
  relationships?: Array<{
    type: 'blocks' | 'blocked_by' | 'depends_on' | 'dependency_of' | 'aggregates' | 'part_of';
    otherItemTitle: string;
    otherItemRank?: number;
    status: string;
  }>;
}

export interface ResultsExplainerContext {
  // Session identity
  sessionName: string;
  sessionIntent: string;
  toolName: string;
  completedAt?: string;

  // Participants
  participantCount: number;
  participantNames?: string[];

  // Cycle / track context
  cycleName?: string;
  trackName?: string;
  configurationName?: string;

  // Criteria
  criteriaIds: string[];
  criteriaLabels?: Record<string, string>;
  criteriaWeights?: Record<string, number>;

  // Results
  totalItems: number;
  cutLinePosition?: number;
  rankedItems: RankedItemDetail[];

  // Relationships among the ranked items
  activeRelationships: Array<{
    sourceTitle: string;
    sourceRank?: number;
    targetTitle: string;
    targetRank?: number;
    relationshipType: string;
    status: string;
  }>;
}

export interface ResultsExplanation {
  headline: string;
  executiveSummary: string;
  keyInsights: string[];
  topItemNarratives: Array<{
    rank: number;
    title: string;
    narrative: string;
  }>;
  relationshipNotes?: string;
  participantAlignment?: string;
  notablePatterns?: string;
}

function buildExplainerPrompt(ctx: ResultsExplainerContext): string {
  const itemList = ctx.rankedItems.slice(0, 15).map(item => {
    const relNotes = item.relationships?.length
      ? ` | Relationships: ${item.relationships.map(r => `${r.type} "${r.otherItemTitle}" (rank #${r.otherItemRank ?? '?'})`).join(', ')}`
      : '';
    const voteNote = item.votes !== undefined ? ` | Votes: ${item.votes}` : '';
    const scoreNote = item.score !== undefined ? ` | Score: ${item.score.toFixed(2)}` : '';
    return `#${item.rank} ${item.title}${scoreNote}${voteNote}${relNotes}`;
  }).join('\n');

  const criteriaList = ctx.criteriaIds.map(id => {
    const label = ctx.criteriaLabels?.[id] ?? id;
    const weight = ctx.criteriaWeights?.[id];
    return weight != null ? `${label} (weight: ${weight.toFixed(2)})` : label;
  }).join(', ');

  const relList = ctx.activeRelationships.length > 0
    ? ctx.activeRelationships.map(r =>
        `"${r.sourceTitle}" (rank #${r.sourceRank ?? '?'}) ${r.relationshipType} "${r.targetTitle}" (rank #${r.targetRank ?? '?'}) [${r.status}]`
      ).join('\n')
    : 'None';

  const cutNote = ctx.cutLinePosition
    ? `Cut line after rank #${ctx.cutLinePosition} (${ctx.cutLinePosition} above the line, ${ctx.totalItems - ctx.cutLinePosition} below)`
    : '';

  return `You are a prioritization analyst summarizing the results of a structured group decision session.

Session: ${ctx.sessionName}
Tool used: ${ctx.toolName}
${ctx.cycleName ? `Cycle: ${ctx.cycleName}` : ''}${ctx.trackName ? `\nTrack: ${ctx.trackName}` : ''}
${ctx.configurationName ? `Configuration: ${ctx.configurationName}` : ''}
Participants: ${ctx.participantCount}${ctx.participantNames?.length ? ` (${ctx.participantNames.join(', ')})` : ''}
Criteria: ${criteriaList || 'none'}
${cutNote}

Ranked items:
${itemList}

Relationships among ranked items:
${relList}

Produce a structured explanation of these results for stakeholders who were not in the session. Be specific — reference actual item names and ranks. Note where relationships between items may be significant. Note if there was likely disagreement among participants (wide score distributions). Be concise and insightful, not generic.

Return JSON only:
{
  "headline": "One specific sentence summarizing the key outcome",
  "executiveSummary": "2-3 sentences for leadership. What was decided and what matters most.",
  "keyInsights": ["Specific observation 1", "Specific observation 2", "...up to 5"],
  "topItemNarratives": [
    { "rank": 1, "title": "item name", "narrative": "1-2 sentences on why this ranked here" }
  ],
  "relationshipNotes": "Paragraph on how item relationships affected or contextualized the ranking. Omit if no relationships.",
  "participantAlignment": "Commentary on where the group agreed strongly vs where scores diverged. Omit if no score distribution data.",
  "notablePatterns": "Anything surprising or worth investigating. Omit if nothing notable."
}`;
}

function parseExplanationResponse(content: string): ResultsExplanation | null {
  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;
    const parsed = JSON.parse(jsonMatch[0]);
    if (typeof parsed.headline !== 'string') return null;

    return {
      headline: parsed.headline.trim(),
      executiveSummary:
        typeof parsed.executiveSummary === 'string' ? parsed.executiveSummary.trim() : '',
      keyInsights: Array.isArray(parsed.keyInsights)
        ? parsed.keyInsights
            .filter((k: unknown) => typeof k === 'string')
            .slice(0, 5)
            .map((k: string) => k.trim())
        : [],
      topItemNarratives: Array.isArray(parsed.topItemNarratives)
        ? parsed.topItemNarratives
            .filter(
              (r: unknown) =>
                r &&
                typeof r === 'object' &&
                typeof (r as Record<string, unknown>).rank === 'number' &&
                typeof (r as Record<string, unknown>).narrative === 'string'
            )
            .slice(0, 5)
            .map((r: Record<string, unknown>) => ({
              rank: r.rank as number,
              title: typeof r.title === 'string' ? r.title.trim() : '',
              narrative: (r.narrative as string).trim(),
            }))
        : [],
      relationshipNotes:
        typeof parsed.relationshipNotes === 'string' && parsed.relationshipNotes.trim()
          ? parsed.relationshipNotes.trim()
          : undefined,
      participantAlignment:
        typeof parsed.participantAlignment === 'string' && parsed.participantAlignment.trim()
          ? parsed.participantAlignment.trim()
          : undefined,
      notablePatterns:
        typeof parsed.notablePatterns === 'string' && parsed.notablePatterns.trim()
          ? parsed.notablePatterns.trim()
          : undefined,
    };
  } catch (e) {
    console.warn('[llmResultsExplainerService] Parse failed:', e);
    return null;
  }
}

const EXPLANATION_UNAVAILABLE =
  'Could not generate explanation — no LLM provider available or response was invalid.';

const MEMO_SYSTEM_PROMPT =
  'You write short, plain prioritization memos for people who were not in the room. You never put words in anyone\'s mouth. Respond with valid JSON only.';

/**
 * The memo's one model call (Brief K slice 3; prompt and validation in
 * src/lib/prioritizationMemo.ts). Same registry capability as the other
 * explanations — results_explainer is the one generator (owner ruling K5).
 *
 * Returns null, never throws: the memo is written without the AI's prose when
 * the assist is off or no provider answers, and says why.
 */
export async function explainForMemo(
  prompt: string,
  workspaceId?: string | null,
): Promise<{ raw: string | null; note: string | null }> {
  if (!isAiCapabilityEnabled('results_explainer', workspaceId)) {
    return { raw: null, note: 'The results explainer is switched off for this workspace, so this memo carries only what was recorded.' };
  }
  const raw = await callFeatureLlm({
    userPrompt: prompt,
    systemPrompt: MEMO_SYSTEM_PROMPT,
    conversationId: 'prioritization-memo-' + Date.now(),
    temperature: 0.3,
    maxTokens: 1800,
  });
  return raw
    ? { raw, note: null }
    : { raw: null, note: 'No AI provider answered, so this memo carries only what was recorded. Regenerate to try again.' };
}

/**
 * Generate a rich, contextual explanation of session results.
 * Throws on failure (caller must handle and show error state — no silent failure).
 */
export async function explainResults(
  context: ResultsExplainerContext
): Promise<ResultsExplanation> {
  if (!isAiCapabilityEnabled('results_explainer') || context.rankedItems.length === 0) {
    throw new Error('LLM explanation is disabled or no ranked items to explain.');
  }

  const content = await callFeatureLlm({
    userPrompt: buildExplainerPrompt(context),
    systemPrompt: RESULTS_EXPLAINER_SYSTEM_PROMPT,
    conversationId: 'results-explainer-' + Date.now(),
    model: DEFAULT_MODEL,
    temperature: 0.4,
    maxTokens: 900,
  });

  const result = content ? parseExplanationResponse(content) : null;
  if (!result) {
    throw new Error(EXPLANATION_UNAVAILABLE);
  }

  return result;
}
